/**
 * WeatherTile — current temp, feels-like, hourly strip, footer.
 *
 * Plan 01-14 Task 2.  Fills the `data-tile="weather"` slot in IdleMode.
 *
 * Backend contract: `GET /api/weather/current` returns the WeatherView
 * (Plan 07 D-12 — never raw Met.no body). The hook `useWeather()` consumes
 * this directly; no further client-side transform.
 *
 * Stell_Kjokken (#160) styrer utseendet:
 *   - symbol 52 px, temperatur `text-display-m` (56 px) og «Føles som»
 *   - fem timer fremover som klokkeslett + temperatur (uten ikon)
 *   - bunnlinje: nedbør, vind og kilden (yr.no)
 *   - «Vær kan være utdatert» når `data.stale === true`
 * Utelatt etter PLAN.md steg 10a: H/L (hi_lo er maks/min over de neste 24
 * timene, ikke dagens) og værbeskrivelsen («Delvis skyet»), som vi ikke har.
 *
 * Error contract: backend serves stale body during a Met.no outage with
 * `stale: true`. A true network error here means BACKEND unreachable —
 * we render `t('weather.unavailable')` (warm Norwegian fallback per D-05).
 */
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/primitives/Card';
import { Pill } from '../../../components/primitives/Pill';
import { WxIcon } from '../../../components/primitives/WxIcon';
import { formatDecimal, formatTime } from '../../../i18n/formatters';
import { useWeather } from '../useWeather';

/** Glasskortet fra skissen: radius 28, luft 22. */
const WX_CARD = 'rounded-xl p-5.5';

export function WeatherTile() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useWeather();

  if (isLoading) {
    return (
      <Card variant="soft" className={WX_CARD}>
        <div data-testid="wx-loading" className="animate-pulse bg-inset h-40 w-full rounded-lg" />
      </Card>
    );
  }

  if (error || !data || !data.current) {
    return (
      <Card variant="soft" className={WX_CARD}>
        <p className="text-ink-2 text-body" data-testid="wx-unavailable">
          {t('weather.unavailable')}
        </p>
      </Card>
    );
  }

  return (
    <Card variant="soft" className={`${WX_CARD} flex flex-col gap-4`}>
      <div className="flex items-center gap-4">
        <WxIcon code={data.current.symbol_code} size={52} />
        <span
          data-testid="wx-temp"
          className="text-display-m tnum text-ink leading-none font-extrabold tracking-tight"
        >
          {formatDecimal(data.current.temperature)}°
        </span>
        <p className="text-ink-2 m-0 text-body leading-snug first-letter:uppercase">
          {t('weather.feels_like', {
            temp: formatDecimal(data.current.feels_like),
          })}
        </p>
        {data.stale ? (
          <Pill variant="neutral" className="ml-auto">
            {t('weather.stale')}
          </Pill>
        ) : null}
      </div>

      <ol
        aria-label={t('weather.next_hours')}
        className="m-0 grid list-none grid-cols-5 p-0 text-center"
      >
        {data.hourly.slice(0, 5).map((h) => (
          <li key={h.time} data-testid="wx-hourly-cell" className="flex flex-col gap-1">
            <span className="text-ink-2 tnum text-body-s">{formatTime(new Date(h.time))}</span>
            <span className="text-ink tnum text-lg font-extrabold">
              {formatDecimal(h.temperature)}°
            </span>
          </li>
        ))}
      </ol>

      <p data-testid="wx-footer" className="text-ink-2 tnum m-0 text-body-s">
        {t('weather.footer', {
          mm: formatDecimal(data.current.precipitation_mm),
          wind: formatDecimal(data.current.wind_ms),
        })}
      </p>
    </Card>
  );
}
